import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'

import { useIsMobileVariant, useSnackbar } from '@dhl-ui/components'

import { useAcknowledgeWhatsNewFeature } from '@dhl/receiver-preference-api'
import { links } from '~/links.js'

import { AgreedPlacePhotoInteractionDialog } from './agreed-place-photo/AgreedPlacePhotoInteractionDialog.js'
import { useAgreedPlacePhotoInteraction } from './agreed-place-photo/useAgreedPlacePhotoInteraction.js'
import { GuidedTourInteraction } from './guided-tour/GuidedTourInteraction.js'
import { useGuidedTourInteraction } from './guided-tour/useGuidedTourInteraction.js'
import {
  type InteractionConfig,
  AgreedPlacePhotoInteractionType,
  GuidedTourInteractionType,
  NewServicePointInteractionType,
  ValidateMobileNumberInteractionType,
} from './interaction.js'
import { ValidateMobileNumberInteraction } from './validate-profile/ValidateMobileNumberInteraction.js'
import { useValidateMobileNumberInteraction } from './validate-profile/useValidateMobileNumberInteraction.js'
import { useNewServicePointInteraction } from './new-servicepoint/useNewServicePointInteraction.js'
import { NewServicePointInteraction } from './new-servicepoint/NewServicePointInteraction.js'

type Location = {
  referer?: string
}

/**
 * The following hooks return two items:
 *
 * 1. The order and configuration of interactions. The priority is from high to low. Each interaction includes a type,
 *    a `shouldExecute` function, and a Component. The `shouldExecute` function is used to determine which interaction
 *    should be the next to display in the UI, as managed by the Interaction Manager. Once an interaction is selected,
 *    the configured component is rendered.
 *
 * 2. In addition to interactions, the configuration includes an option called `enabledPaths`. This setting
 *    determines when the Interaction Manager should (re)check if an interaction needs to be displayed.
 */
export const useInteractionConfig = (): InteractionConfig => {
  const { mutateAsync: deleteAgreedPlacePictureFeature } = useAcknowledgeWhatsNewFeature()
  const { enqueueSnackbar } = useSnackbar()
  const { t } = useTranslation()
  const { pathname, state } = useLocation()
  const isMobile = useIsMobileVariant()

  const hasNonSetupReferrer = (state as Location)?.referer !== 'setup'

  const handleAgreedPlacePhotoInteractionComplete = async () => {
    try {
      await deleteAgreedPlacePictureFeature({ feature: 'agreed-place-picture' })
    } catch {
      enqueueSnackbar(t('common.pleaseTryAgainErrorMessage'), { severity: 'error' })
    }
  }

  const shouldExecuteAgreedPlacePhotoInteraction = useAgreedPlacePhotoInteraction()
  const shouldExecuteGuidedTourInteraction = useGuidedTourInteraction()
  const shouldExecuteNewServicePointInteraction = useNewServicePointInteraction()
  const shouldExecuteValidateMobileNumberInteraction = useValidateMobileNumberInteraction()

  return {
    interactions: [
      {
        type: ValidateMobileNumberInteractionType,
        shouldExecute: shouldExecuteValidateMobileNumberInteraction,
        component: <ValidateMobileNumberInteraction />,
      },
      {
        type: AgreedPlacePhotoInteractionType,
        shouldExecute: async () =>
          isMobile &&
          hasNonSetupReferrer &&
          [links.dashboardLink(), links.incomingPiecesLink()].includes(pathname) &&
          shouldExecuteAgreedPlacePhotoInteraction(),
        component: (
          <AgreedPlacePhotoInteractionDialog onCompleteInteraction={handleAgreedPlacePhotoInteractionComplete} />
        ),
      },
      {
        type: GuidedTourInteractionType,
        shouldExecute: shouldExecuteGuidedTourInteraction,
        component: <GuidedTourInteraction />,
      },
      {
        type: NewServicePointInteractionType,
        shouldExecute: shouldExecuteNewServicePointInteraction,
        component: <NewServicePointInteraction />,
      },
    ],
  }
}
